import { rgba } from 'polished';
import styled, { keyframes } from 'styled-components';

/* ===== Page layout ===== */

export const StyledTopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  position: relative;
  padding: 1.5rem 1.5rem;
  margin: -1rem -1rem 1.5rem;
  border-radius: 1rem;
  background: linear-gradient(
      135deg,
      ${({ theme }) => rgba(theme.colors.brand.navy, 0.04)} 0%,
      ${({ theme }) => rgba(theme.colors.brand.yellow, 0.06)} 100%
    );
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -2rem;
    right: -2rem;
    width: 14rem;
    height: 14rem;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 280' fill='none'><g stroke='%23090F38' stroke-width='1.6' stroke-linejoin='round' stroke-linecap='round' opacity='0.18'><path d='M40 80l60-30 60 30v60l-60 30-60-30z'/><path d='M40 80l60 30M160 80l-60 30M100 110v60'/><path d='M30 70l-12-3M30 70l-10 8M40 80l-18-2'/></g></svg>");
    background-repeat: no-repeat;
    background-size: contain;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const StyledSearchForm = styled.form`
  flex: 1;
  max-width: ${({ theme }) => theme.dimensions.searchForm.width};
  z-index: ${({ theme }) => theme.zindexes.search};
`;

export const StyledCartButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.background.medium};
  background: ${({ theme }) => theme.colors.background.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 180ms cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => rgba(theme.colors.accent, 0.06)};
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
`;

export const StyledCartBadge = styled.span`
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 1.1rem;
  height: 1.1rem;
  border-radius: 999px;
  padding: 0 0.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledCategoryBar = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

/* === Brands row === */

export const StyledBrandsSection = styled.section`
  margin-bottom: 1.75rem;
`;

export const StyledBrandsHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.875rem;
`;

export const StyledBrandsTitle = styled.h2`
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => rgba(theme.colors.text.secondary, 0.7)};
  margin: 0;
`;

export const StyledBrandsClear = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  color: ${({ theme }) => theme.colors.accent};
  padding: 0;
  transition: opacity 180ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    opacity: 0.75;
  }
`;

export const StyledBrandsScroll = styled.div`
  display: flex;
  gap: 0.875rem;
  overflow-x: auto;
  padding: 0.25rem 0.125rem 0.5rem;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }
`;

export const StyledBrandTile = styled.button<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem 0.5rem;
  border-radius: 0.625rem;
  border: 1px solid
    ${({ theme, active }) =>
      active ? theme.colors.accent : theme.colors.background.medium};
  background: ${({ theme, active }) =>
    active ? rgba(theme.colors.accent, 0.06) : theme.colors.background.light};
  cursor: pointer;
  flex-shrink: 0;
  scroll-snap-align: start;
  transition: all 180ms cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 5.5rem;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px -4px rgba(16, 24, 40, 0.08);
  }
`;

export const StyledBrandLogo = styled.div<{ active?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, active }) =>
    active
      ? rgba(theme.colors.accent, 0.14)
      : rgba(theme.colors.text.primary, 0.05)};
  color: ${({ theme, active }) =>
    active ? theme.colors.accent : theme.colors.text.secondary};
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid
    ${({ theme, active }) =>
      active
        ? rgba(theme.colors.accent, 0.32)
        : rgba(theme.colors.text.primary, 0.06)};
  transition: all 180ms cubic-bezier(0.4, 0, 0.2, 1);
`;

export const StyledBrandName = styled.span<{ active?: boolean }>`
  font-size: 0.75rem;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  color: ${({ theme, active }) =>
    active ? theme.colors.accent : theme.colors.text.primary};
  letter-spacing: -0.005em;
  white-space: nowrap;
`;

export const StyledBrandCount = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => rgba(theme.colors.text.secondary, 0.6)};
  font-variant-numeric: tabular-nums;
`;

export const StyledCategoryChip = styled.button<{ active?: boolean }>`
  padding: 0.375rem 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${({ theme, active }) =>
      active ? theme.colors.brand.yellow : theme.colors.background.medium};
  background: ${({ theme, active }) =>
    active
      ? rgba(theme.colors.brand.yellow, 0.16)
      : theme.colors.background.light};
  color: ${({ theme, active }) =>
    active ? theme.colors.brand.navy : theme.colors.text.primary};
  font-size: 0.8125rem;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand.yellow};
    color: ${({ theme }) => theme.colors.brand.navy};
    background: ${({ theme, active }) =>
      active
        ? rgba(theme.colors.brand.yellow, 0.22)
        : rgba(theme.colors.brand.yellow, 0.08)};
  }
`;

/* ===== Card grid ===== */

export const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  gap: 1.25rem;
`;

export const StyledCard = styled.div`
  background: ${({ theme }) => theme.colors.background.light};
  border-radius: 0.625rem;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.background.medium};
  display: flex;
  flex-direction: column;
  transition: box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 200ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 200ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 8px 20px -8px rgba(16, 24, 40, 0.1);
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &:hover > div:first-child > * {
    transform: scale(1.06);
  }
`;

export const StyledCardImage = styled.div<{ bg?: string }>`
  height: 8rem;
  background: ${({ theme }) => theme.colors.background.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  overflow: hidden;

  > * {
    transition: transform 320ms cubic-bezier(0.4, 0, 0.2, 1);
    display: inline-block;
    filter: grayscale(0);
  }
`;

export const StyledCardBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const StyledCardCategory = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => rgba(theme.colors.text.secondary, 0.7)};
  margin-bottom: 0.375rem;
`;

export const StyledCardTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 0.375rem;
  line-height: 1.3;
  letter-spacing: -0.005em;
`;

export const StyledCardDescription = styled.p`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 0.875rem;
  line-height: 1.45;
  flex: 1;
`;

export const StyledCardFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: auto;
`;

export const StyledPriceBlock = styled.div``;

export const StyledPrice = styled.div`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.005em;
  display: inline-flex;
  align-items: baseline;
  gap: 0.3125rem;

  small {
    font-size: 0.6875rem;
    font-weight: 500;
    color: ${({ theme }) => rgba(theme.colors.text.secondary, 0.6)};
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;

export const StyledMinQty = styled.div`
  font-size: 0.72rem;
  color: ${({ theme }) => rgba(theme.colors.text.secondary, 0.7)};
  margin-top: 0.1875rem;
  font-variant-numeric: tabular-nums;
`;

export const StyledBuyButton = styled.button`
  padding: 0.4375rem 0.875rem;
  border-radius: 0.375rem;
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  border: none;
  cursor: pointer;
  transition: filter 180ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);

  &:hover {
    filter: brightness(0.96);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px -2px rgba(16, 24, 40, 0.1);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  }
`;

export const StyledEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9375rem;
`;

/* ===== Overlay shared ===== */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(1rem); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideRight = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

export const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(9, 15, 55, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  animation: ${fadeIn} 0.18s ease;
`;

/* ===== Product detail modal ===== */

export const StyledModalCard = styled.div`
  background: ${({ theme }) => theme.colors.background.light};
  border-radius: 1rem;
  width: 100%;
  max-width: 52rem;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.22s ease;
  display: flex;
  flex-direction: column;
`;

export const StyledModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.medium};
`;

export const StyledModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const StyledCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: background 0.15s ease;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.default};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const StyledProductDetailBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 1.5rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledProductImageLarge = styled.div<{ bg: string }>`
  background: ${({ bg }) => bg};
  border-radius: 0.75rem;
  height: 18rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 6rem;
`;

export const StyledProductInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledFullDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.55;
  margin: 0.5rem 0 1rem;
`;

export const StyledSpecsList = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem 1rem;
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;

  dt {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 500;
  }

  dd {
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
    font-weight: 500;
  }
`;

export const StyledCertBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

export const StyledCertBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  background: ${({ theme }) => theme.colors.background.default};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: 0.25rem;
`;

export const StyledQtyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.background.default};
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
  margin-top: 1rem;
`;

export const StyledQtyLabel = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    display: block;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 0.125rem;
    font-weight: 600;
  }
`;

export const StyledQtyStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StyledStepperButton = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  border: 1.5px solid ${({ theme }) => theme.colors.background.medium};
  background: ${({ theme }) => theme.colors.background.light};
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const StyledQtyInput = styled.input`
  width: 4rem;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: 600;
  border: 1.5px solid ${({ theme }) => theme.colors.background.medium};
  border-radius: 0.375rem;
  padding: 0.375rem;
  background: ${({ theme }) => theme.colors.background.light};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

export const StyledTotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.background.medium};
  background: ${({ theme }) => theme.colors.background.default};
`;

export const StyledTotalLabel = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StyledTotalValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const StyledModalFooter = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.background.medium};
`;

export const StyledSecondaryButton = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.background.light};
  border: 1.5px solid ${({ theme }) => theme.colors.background.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
  }
`;

export const StyledPrimaryButton = styled.button`
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ===== Cart drawer ===== */

export const StyledDrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(9, 15, 55, 0.45);
  z-index: 1000;
  animation: ${fadeIn} 0.18s ease;
`;

export const StyledDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 25rem;
  background: ${({ theme }) => theme.colors.background.light};
  z-index: 1001;
  display: flex;
  flex-direction: column;
  animation: ${slideRight} 0.22s ease;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.15);
`;

export const StyledDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.medium};
`;

export const StyledDrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
`;

export const StyledDrawerFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.background.medium};
  padding: 1.25rem 1.5rem;
  background: ${({ theme }) => theme.colors.background.default};
`;

export const StyledCartItem = styled.div`
  display: grid;
  grid-template-columns: 3rem 1fr auto;
  gap: 0.875rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.medium};
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

export const StyledCartItemImage = styled.div<{ bg: string }>`
  width: 3rem;
  height: 3rem;
  background: ${({ bg }) => bg};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

export const StyledCartItemInfo = styled.div`
  min-width: 0;
`;

export const StyledCartItemName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledCartItemMeta = styled.div`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StyledCartItemPrice = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
  white-space: nowrap;
`;

export const StyledRemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.7rem;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 0.25rem;
  padding: 0;
  text-align: right;
  display: block;
  margin-left: auto;

  &:hover {
    color: ${({ theme }) => theme.colors.text.error};
  }
`;

export const StyledCartTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1rem;
`;

export const StyledCartTotalLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StyledCartTotalValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const StyledCheckoutButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  border: none;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const StyledEmptyCart = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;

  div:first-child {
    font-size: 3rem;
    margin-bottom: 0.75rem;
  }
`;

/* ===== Checkout ===== */

export const StyledCheckoutBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 22rem;
  gap: 1.5rem;
  padding: 1.5rem;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledCheckoutSection = styled.section`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const StyledCheckoutSectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 0.75rem;
`;

export const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledFormGroup = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ fullWidth }) => fullWidth && 'grid-column: 1 / -1;'}
`;

export const StyledLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 0.375rem;
`;

export const StyledFormInput = styled.input`
  padding: 0.625rem 0.75rem;
  border: 1.5px solid ${({ theme }) => theme.colors.background.medium};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.background.light};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

export const StyledTextarea = styled.textarea`
  padding: 0.625rem 0.75rem;
  border: 1.5px solid ${({ theme }) => theme.colors.background.medium};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.background.light};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
  min-height: 4rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

export const StyledPaymentOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const StyledPaymentOption = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid
    ${({ theme, selected }) =>
      selected ? theme.colors.accent : theme.colors.background.medium};
  border-radius: 0.5rem;
  cursor: pointer;
  background: ${({ theme, selected }) =>
    selected ? `${theme.colors.accent}11` : theme.colors.background.light};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
  transition: all 0.15s ease;

  input {
    accent-color: ${({ theme }) => theme.colors.accent};
  }

  span {
    font-size: 1.125rem;
    margin-right: 0.25rem;
  }
`;

export const StyledOrderSummary = styled.aside`
  background: ${({ theme }) => theme.colors.background.default};
  border-radius: 0.75rem;
  padding: 1.25rem;
  align-self: start;
  position: sticky;
  top: 0;
`;

export const StyledSummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.medium};

  &:last-of-type {
    border-bottom: none;
  }
`;

export const StyledSummaryItemInfo = styled.div`
  flex: 1;
  padding-right: 0.5rem;

  strong {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
    margin-bottom: 0.125rem;
  }
  span {
    font-size: 0.72rem;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const StyledSummaryRow = styled.div<{ bold?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: ${({ bold }) => (bold ? '1rem' : '0.8125rem')};
  font-weight: ${({ bold }) => (bold ? 700 : 500)};
  color: ${({ theme }) => theme.colors.text.primary};
  border-top: ${({ bold, theme }) =>
    bold ? `1px solid ${theme.colors.background.medium}` : 'none'};
  margin-top: ${({ bold }) => (bold ? '0.5rem' : 0)};
  padding-top: ${({ bold }) => (bold ? '0.75rem' : '0.5rem')};
`;

/* ===== Success ===== */

export const StyledSuccessBox = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StyledSuccessIcon = styled.div`
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 2rem;
  margin-bottom: 1.25rem;
`;

export const StyledSuccessTitle = styled.h2`
  font-size: 1.375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 0.5rem;
`;

export const StyledSuccessMessage = styled.p`
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 1.5rem;
  max-width: 28rem;
`;

export const StyledOrderRef = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.default};
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;

  span {
    color: ${({ theme }) => theme.colors.accent};
  }
`;
